import React, { useState } from 'react';
import { useAppContext, View } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PrivacyBadge } from '../ui/PrivacyBadge';
import { InvitePlayerModal } from '../modals/InvitePlayerModal';
import { getUserRole, getRoleLabel, hasPermission } from '../../utils/permissions';
import { usePendingJoinRequestsCount } from '../../hooks/usePendingJoinRequestsCount';
import { useTranslation } from 'react-i18next';
import { generateSlug } from '../../utils/stringUtils';

export const GroupInfo: React.FC = () => {
    const { currentGroup, goToPage, db } = useAppContext();
    const { user } = useAuth();
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const { t } = useTranslation();

    // Count pending join requests for badge - ONLY if user has permission to manage group
    const canManageGroup = React.useMemo(() => {
        if (!user || !currentGroup) return false;
        return hasPermission(user, 'manage_group_settings', { group: currentGroup, globalConfig: db.globalConfig }) ||
            hasPermission(user, 'manage_group_members', { group: currentGroup, globalConfig: db.globalConfig });
    }, [user, currentGroup, db.globalConfig]);

    // Count pending join requests for badge - ONLY if user has permission to manage group
    const { count: pendingRequestsCount } = usePendingJoinRequestsCount(
        (canManageGroup && currentGroup?.privacyMode === 'request') ? currentGroup?.id : undefined
    );

    if (!currentGroup) return null;

    const handleShare = () => {
        const urlSlug = currentGroup.slug || generateSlug(currentGroup.name);
        const groupUrl = `${window.location.origin}/group/${urlSlug}`;
        
        const shareData = {
            title: t('groups.shareTitle', 'Rejoins mon groupe Padel Brothers !'),
            text: t('groups.shareText', 'Rejoins le groupe "{{name}}" sur Padel Brothers avec le code : {{code}}', { name: currentGroup.name, code: currentGroup.code }),
            url: groupUrl
        };
        if (navigator.share) {
            navigator.share(shareData).catch(console.error);
        } else {
            navigator.clipboard.writeText(`${shareData.text} \n${shareData.url}`);
            alert(t('groups.shareCopied', 'Lien copié dans le presse-papier !'));
        }
    };

    const isOfficial = currentGroup.id === 'official_padel_brothers';

    // Check if user is admin for invite button
    const userId = user?.uid || '';
    const isAdmin = currentGroup.ownerId === userId ||
        currentGroup.admins?.includes(userId) ||
        currentGroup.players?.find(p => p.id === userId)?.groupRole === 'admin';

    // Show invite button only for invite-only groups where user is admin
    const showInviteButton = isAdmin && currentGroup.privacyMode === 'invite';

    // Get existing member IDs to exclude from search
    const existingMemberIds = [
        ...(currentGroup.players?.map(p => p.id) || []),
        ...(currentGroup.members || [])
    ];

    return (
        <div className="relative z-10 mt-3 overflow-hidden rounded-lg mx-2">
            {/* Fading Background Layer */}
            <div
                className="absolute inset-0 bg-surface-1 backdrop-blur-xl shadow-sm"
                style={{
                    maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
                    WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
                }}
            >
                {/* Optional: Subtle Gradient Overlay inside the masked area */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5"></div>

                {/* Background Image (if any) - also masked */}
                {currentGroup.logoUrl && (currentGroup.logoUrl.startsWith('http') || currentGroup.logoUrl.startsWith('data:')) && (
                    <div
                        className="absolute inset-0 bg-cover bg-center opacity-20 blur-2xl scale-110"
                        style={{ backgroundImage: `url('${currentGroup.logoUrl}')` }}
                    ></div>
                )}
            </div>

            <div className="relative max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
                {/* Left: Group Identity */}
                <div className="flex items-center gap-4">
                    {/* Logo (if available) or Icon */}
                    <div className={`w-14 h-14 rounded-2xl bg-surface-2/40 backdrop-blur-md flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-500 ${isOfficial ? 'border-2 border-yellow-400' : 'ring-1 ring-white/10'}`}>
                        {isOfficial ? (
                            <img src="/official_logo.webp" alt="Official Logo" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                        ) : currentGroup.logoUrl ? (
                            (currentGroup.logoUrl.startsWith('http') || currentGroup.logoUrl.startsWith('data:')) ? (
                                <img src={currentGroup.logoUrl} alt="Logo" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                            ) : (
                                <span className="text-3xl">{currentGroup.logoUrl}</span>
                            )
                        ) : (
                            <span className="material-symbols-outlined text-3xl text-primary/80">groups</span>
                        )}
                    </div>

                    <div className="flex flex-col justify-center">
                        <div className="flex items-center gap-2">
                            <h2 className={`text-xl font-black leading-none tracking-tight drop-shadow-sm ${isOfficial ? 'text-primary' : 'text-text-primary'}`}>
                                {currentGroup.name}
                            </h2>
                            {isOfficial && (
                                <span className="material-symbols-outlined text-yellow-400 text-lg" title="Groupe Officiel">verified</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                            <div className="flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-surface-2/40 backdrop-blur-sm border border-white/5 shadow-sm">
                                <span className="text-xs font-bold text-primary tracking-widest font-mono">
                                    {currentGroup.code || (currentGroup.id?.startsWith('imported_') ? `IMP-${currentGroup.id.slice(-4).toUpperCase()}` : currentGroup.id)}
                                </span>
                            </div>

                            {!isOfficial && (
                                <>
                                    {(() => {
                                        const isMember = (currentGroup.players || []).some(p => p.id === user?.uid) || (currentGroup.members || []).includes(user?.uid || '');

                                        if (isMember) {
                                            const role = getUserRole(user, currentGroup);
                                            // Special badges for specific roles
                                            if (role !== 'player' && role !== 'user') {
                                                const label = role === 'admin' ? 'Admin' :
                                                    role === 'co_admin' ? 'Co-Admin' :
                                                        role === 'organizer' ? 'Organisateur' :
                                                            role === 'referee' ? 'Arbitre' :
                                                                role === 'owner' ? 'Propriétaire' : 'Membre';

                                                const colorClass = role === 'admin' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' :
                                                    role === 'co_admin' ? 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20' :
                                                        role === 'organizer' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
                                                            role === 'referee' ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' :
                                                                'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'; // Owner

                                                const icon = role === 'admin' ? 'shield' :
                                                    role === 'co_admin' ? 'security' :
                                                        role === 'organizer' ? 'emoji_events' :
                                                            role === 'referee' ? 'sports' : 'workspace_premium';

                                                return (
                                                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${colorClass} shadow-sm backdrop-blur-sm`} title={getRoleLabel(role)}>
                                                        <span className="material-symbols-outlined text-sm font-bold icon-filled" style={{ fontSize: '14px' }}>
                                                            {icon}
                                                        </span>
                                                        <span className="text-[10px] font-bold uppercase">
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            }
                                        }

                                        // Default fallback: PrivacyBadge (Handles "Membre" for basic players, and Privacy for non-members)
                                        return (
                                            <PrivacyBadge
                                                mode={currentGroup.privacyMode}
                                                className="bg-surface-2/40 backdrop-blur-sm border-white/5 shadow-sm"
                                                isMember={isMember}
                                            />
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Actions */}
                <div className="flex flex-col gap-2 items-center">
                    {/* Admin Button (Moved from Navbar) */}
                    {canManageGroup && (
                        <button
                            onClick={() => goToPage(View.GROUP_ADMIN)}
                            className="w-10 h-10 rounded-full bg-surface-2/40 backdrop-blur-md border border-white/10 text-text-primary hover:bg-primary hover:text-white hover:border-primary/50 transition-all shadow-lg flex items-center justify-center active:scale-95 group relative"
                            title={t('navbar.admin', 'Admin')}
                        >
                            <span className="material-symbols-outlined text-xl group-hover:rotate-12 transition-transform">admin_panel_settings</span>
                            {pendingRequestsCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1 border-2 border-surface-1 shadow-sm">
                                    {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                                </span>
                            )}
                        </button>
                    )}

                    {/* Invite Button (for admins on invite-only groups) */}
                    {showInviteButton && (
                        <button
                            onClick={() => setInviteModalOpen(true)}
                            className="w-10 h-10 rounded-full bg-primary/20 backdrop-blur-md border border-primary/30 text-primary hover:bg-primary hover:text-white transition-all shadow-lg flex items-center justify-center active:scale-95 group"
                            title={t('groups.inviteTooltip', 'Inviter un joueur')}
                        >
                            <span className="material-symbols-outlined text-lg">person_add</span>
                        </button>
                    )}

                    <button
                        onClick={handleShare}
                        className="w-10 h-10 rounded-full bg-surface-2/40 backdrop-blur-md border border-white/10 text-text-primary hover:bg-primary hover:text-white hover:border-primary/50 transition-all shadow-lg flex items-center justify-center active:scale-95 group"
                        title={t('groups.shareTooltip', 'Partager le groupe')}
                    >
                        <span className="material-symbols-outlined text-xl group-hover:animate-pulse">share</span>
                    </button>
                </div>
            </div>

            {/* Invite Modal */}
            <InvitePlayerModal
                isOpen={inviteModalOpen}
                onClose={() => setInviteModalOpen(false)}
                type="group"
                groupId={currentGroup.id}
                groupName={currentGroup.name}
                existingMemberIds={existingMemberIds}
                privacyMode={currentGroup.privacyMode || 'open'}
            />
        </div>
    );
};
